import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { getAuth } from 'firebase/auth';
import { FaComments, FaCheck, FaTimes, FaClock, FaTrash, FaSearch, FaUser, FaMapMarkerAlt, FaUsers, FaCalendarAlt, FaHeart, FaArrowLeft } from 'react-icons/fa';
import { Dropdown } from "react-bootstrap";

import SimpleNavHeader from '../components/SimpleNavHeader';
import '../styles/SearchActivityDetailPage.css';


function SearchActivityDetailPage() {
  const { searchId } = useParams();
  const navigate = useNavigate();
  const [activity, setActivity] = useState(null);
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [memberNames, setMemberNames] = useState({}); // usernames of group members

  const currentUser = getAuth().currentUser;

  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true); // Start loading

      try {
        const activityRef = doc(db, 'search_activities', searchId);
        const activitySnap = await getDoc(activityRef);

        if (!activitySnap.exists()) {
          console.error('No such activity document!');
          return;
        }

        const activityData = activitySnap.data();
        setActivity(activityData);

        if (!activityData.groupId) return;

        const groupRef = doc(db, 'groups', activityData.groupId);
        const groupSnap = await getDoc(groupRef);

        if (!groupSnap.exists()) {
          console.warn('Group not found for groupId:', activityData.groupId);
          return;
        }

        const groupData = groupSnap.data();
        setGroup(groupData);

        // Fetch usernames
        if (groupData.memberStatus) {
          const userIds = Object.keys(groupData.memberStatus);
          const newUsernames = {};

          const fetchPromises = userIds.map(async (userId) => {
            const userRef = doc(db, 'users_public', userId);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
              newUsernames[userId] = userSnap.data().displayName || 'Unnamed';
            } else {
              newUsernames[userId] = 'Unknown User';
            }
          });

          await Promise.all(fetchPromises);
          setMemberNames(newUsernames);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false); // Done loading everything
      }
    };

    fetchAllData();
  }, [searchId]);



  const handleAccept = async () => {
    if (!currentUser || !group || !activity) return;
    setProcessing(true);
    try {
      const groupRef = doc(db, 'groups', activity.groupId);
      const activityRef = doc(db, 'search_activities', searchId);

      await updateDoc(groupRef, {
        [`memberStatus.${currentUser.uid}`]: 'accepted',
      });

      await updateDoc(activityRef, {
        status: 'confirmed',
      });

      navigate(0); // reload
    } catch (error) {
      console.error('Error accepting:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!currentUser || !group || !activity) return;
    setProcessing(true);
    try {
      const groupRef = doc(db, 'groups', activity.groupId);
      const activityRef = doc(db, 'search_activities', searchId);

      await updateDoc(groupRef, {
        [`memberStatus.${currentUser.uid}`]: 'rejected',
      });

      await updateDoc(activityRef, {
        status: 'cancelled',
      });

      navigate('/home'); // reload
    } catch (error) {
      console.error('Error rejecting:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteActivity = async () => {
    if (!currentUser || !activity || !searchId) return;
    setProcessing(true);
    try {
      const activityRef = doc(db, 'search_activities', searchId);
      await updateDoc(activityRef, {
        status: 'cancelled',
      });

      navigate('/home');
    } catch (error) {
      console.error('Error cancelling activity:', error);
    } finally {
      setProcessing(false);
    }
  };


  const getStatusIcon = (status) => {
    switch (status) {
      case 'accepted':
        return <FaCheck className="text-white" style={{ fontSize: '14px' }} />;
      case 'pending':
        return <FaClock className="text-white" style={{ fontSize: '14px' }} />;
      case 'rejected':
        return <FaTimes className="text-white" style={{ fontSize: '14px' }} />;
      default:
        return <FaClock className="text-white" style={{ fontSize: '14px' }} />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'accepted':
        return 'badge bg-success';
      case 'pending':
        return 'badge bg-warning';
      case 'rejected':
        return 'badge bg-danger';
      default:
        return 'badge bg-secondary';
    }
  };

  const generateAvatarClass = (userId) => {
    // const colors = ['primary', 'info', 'success', 'warning', 'danger', 'secondary'];
    let color
    if (userId === currentUser.uid) {
      color = 'primary';
    } else {
      color = 'info';
    }
    return `bg-${color}`;
  };

  const getUserName = (userId) => {
    let username
    if (userId === currentUser.uid) {
      username = 'You';
    } else {
      username = memberNames[userId];
    }
    return username;
  };


  const AcceptRejectComponent = ({ hasAccepted, processing, handleAccept, handleReject }) => {
    const handleChangeStatus = (selectedStatus) => {
      if (selectedStatus === "rejected") {
        handleReject();
      }
    };

    return (
      <div className="d-flex flex-column align-items-center mb-4">
        {!hasAccepted ? (
          <div className="d-flex justify-content-center gap-3">
            <button
              onClick={handleAccept}
              disabled={processing}
              className="btn btn-success btn-custom"
            >
              <FaCheck className="me-2" />
              Accept
            </button>
            <button
              onClick={handleReject}
              disabled={processing}
              className="btn btn-danger btn-custom"
            >
              <FaTimes className="me-2" />
              Reject
            </button>
          </div>
        ) : (
          <div className="d-flex align-items-center gap-2">
            <span className="text-muted">Your status:</span>
            <Dropdown onSelect={handleChangeStatus}>
              <Dropdown.Toggle variant="outline-info" className="btn-custom">
                ✅ Already Accepted
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Item eventKey="accepted">✅ Accepted</Dropdown.Item>
                <Dropdown.Item eventKey="rejected">❌ Change to Rejected</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        )}
      </div>
    );
  };




  if (loading) {
    return (
      <div className="container-fluid min-vh-100 d-flex align-items-center justify-content-center" style={{ background: 'linear-gradient(135deg, #e3f2fd 0%, #ffffff 50%, #f3e5f5 100%)' }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading activity details...</p>
        </div>
      </div>
    );
  }

  if (!activity) {
    return (
      <div className="container-fluid min-vh-100 d-flex align-items-center justify-content-center" style={{ background: 'linear-gradient(135deg, #e3f2fd 0%, #ffffff 50%, #f3e5f5 100%)' }}>
        <div className="text-center">
          <p className="text-muted mb-3">Activity not found.</p>
          <button
            onClick={() => navigate(-1)}
            className="btn btn-primary"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }


  return (
    <>

      <div className="gradient-bg">
        {/* Header */}
        <SimpleNavHeader header_text="Matched Group" />

        <div className="container py-4">
          {/* User Avatars Section */}
          {group ? (
            <div className="card custom-card bg-white mb-4">
              <div className="card-body">
                <div className="text-center mb-1">
                  <h3 className="fw-bold text-dark mb-2">Group Members</h3>

                  {activity.status === "matched" && <p className="text-muted mb-1">Please confirm your participation in the meeting</p>}
                </div>

                {/* Accept/Reject Buttons */}
                {activity.status !== "finished" && <AcceptRejectComponent
                  hasAccepted={activity.status === "confirmed"}
                  processing={processing}
                  handleAccept={handleAccept}
                  handleReject={handleReject}
                />}


                {group.commonAvailability && (
                  <div className="d-flex flex-column align-items-center mb-2">
                    <strong className="text-muted mb-1">Common availability:</strong>
                    <div className="d-flex gap-2 text-muted">
                      <span>
                        {group.commonAvailability.dateFrom === group.commonAvailability.dateTo
                          ? group.commonAvailability.dateFrom
                          : `${group.commonAvailability.dateFrom} — ${group.commonAvailability.dateTo}`}
                      </span>
                      <span>
                        {group.commonAvailability.hourFrom} — {group.commonAvailability.hourTo}
                      </span>
                    </div>
                  </div>
                )}


                <div className="d-flex justify-content-center gap-4 mb-5 flex-wrap">
                  {Object.entries(group.memberStatus).map(([userId, status]) => (
                    <div key={userId} className="text-center">
                      <div className="position-relative d-inline-block">
                        <div className={`avatar-circle ${generateAvatarClass(userId)}`}>
                          {getUserName(userId).charAt(0)}
                        </div>
                        <div className={`status-badge bg-${status === 'accepted' ? 'success' : status === 'pending' ? 'warning' : 'danger'}`}>
                          {getStatusIcon(status)}
                        </div>
                      </div>
                      <div className="mt-1">
                        <p className="fw-medium text-dark mb-0">{getUserName(userId)}</p>
                        <span className={getStatusBadge(status)}>{status}</span>
                      </div>
                    </div>
                  ))}
                </div>



                {/* Start Chat Button */}
                {currentUser && (
                  group.memberStatus[currentUser.uid] === 'accepted' ? (
                    <div className="text-center">
                      <button
                        onClick={() => navigate(`/chat/${activity.groupId}`)}
                        className="btn btn-primary btn-custom"
                      >
                        <FaComments className="me-2" />
                        Chat
                      </button>
                    </div>
                  ) : (
                    <div className="text-center text-muted mt-2">
                      You have to accept the meeting first to be able to go to the chat and agree on details.
                    </div>
                  )
                )}

              </div>
            </div>
          ) : (
            <div className="card custom-card bg-white mb-4">
              <div className="card-body p-3 text-center">
                <p className="text-muted">No matched group found for this activity.</p>
              </div>
            </div>
          )}

          {/* Search Activity Details */}
          <div className="card custom-card bg-white">
            <div className="card-body">



              <div className="card-body p-4">

                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Activity Details</h5>
                  {activity?.status === "pending" && (
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => navigate(`/edit-search/${searchId}`)}
                    >
                      Edit
                    </button>
                  )}
                </div>

                <ul className="list-unstyled small">
                  <li><strong>Activity Type:</strong> {activity.activityType}</li>
                  <li><strong>Location:</strong> {activity.preferredLocation?.lat?.toFixed(5)}, {activity.preferredLocation?.lng?.toFixed(5)}</li>
                  <li><strong>Radius:</strong> {activity.preferredRadiusKm} km</li>
                  <li><strong>Date Range:</strong> {activity.preferredTime?.dateRange
                    ? `${new Date(activity.preferredTime.dateRange.from).toLocaleDateString()} - ${new Date(activity.preferredTime.dateRange.to).toLocaleDateString()}`
                    : 'N/A'}</li>
                  <li><strong>Hour Range:</strong> {activity.preferredTime?.hourRange
                    ? `${activity.preferredTime.hourRange.from} - ${activity.preferredTime.hourRange.to}`
                    : 'N/A'}</li>
                  <li><strong>Number of People:</strong> {activity.numberOfPeople}</li>
                  <li><strong>Status:</strong> <span className={getStatusBadge(activity.status)}>{activity.status}</span></li>
                  <li><strong>Created At:</strong> {activity.createdAt
                    ? new Date(activity.createdAt.seconds * 1000).toLocaleString()
                    : 'N/A'}</li>
                </ul>

                {activity?.status === "pending" && (
                  <div className="text-end mt-3">
                    <button
                      onClick={handleDeleteActivity}
                      disabled={processing}
                      className="btn btn-outline-danger"
                    >
                      <FaTrash className="me-2" />
                      Delete
                    </button>
                  </div>
                )}
              </div>



            </div>
          </div>
        </div>

        {/* Processing Modal */}
        {processing && (
          <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-sm modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-body text-center py-4">
                  <div className="spinner-border text-primary mb-3" role="status">
                    <span className="visually-hidden">Processing...</span>
                  </div>
                  <p className="mb-0">Processing...</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default SearchActivityDetailPage;